/**
 * API Client
 *
 * Handles HTTP requests with automatic token management and refresh.
 */

import type { LoginResponse, RefreshResponse, UserResponse } from '@functieloonhuis/shared';

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Token refresh state to prevent race conditions
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Callback for handling auth failures - will be set by AuthContext
let onAuthFailure: (() => void) | null = null;

/**
 * Register a callback to be called when authentication fails completely
 * (after token refresh also fails)
 */
export function setAuthFailureCallback(callback: () => void): void {
  onAuthFailure = callback;
}

/**
 * Clear the auth failure callback
 */
export function clearAuthFailureCallback(): void {
  onAuthFailure = null;
}

// Callback voor een 403 ROLE_NOT_ASSIGNED: het account bestaat, is ingelogd,
// maar heeft (nog) geen rol in de HR-portal. Dat is GEEN reden om uit te
// loggen — de oude 401 INVALID_ROLE zette de gebruiker in een uitloglus die
// zichzelf nooit herstelde. Spec-fix-rolbepaling-app-scope, punt 5.
let onRoleNotAssigned: ((message: string) => void) | null = null;

/**
 * Register a callback for 403 ROLE_NOT_ASSIGNED (user stays logged in).
 */
export function setRoleNotAssignedCallback(callback: (message: string) => void): void {
  onRoleNotAssigned = callback;
}

export function clearRoleNotAssignedCallback(): void {
  onRoleNotAssigned = null;
}

export const ROLE_NOT_ASSIGNED_CODE = 'ROLE_NOT_ASSIGNED';

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store tokens
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Clear stored tokens
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if we have stored tokens
 */
export function hasTokens(): boolean {
  return !!getAccessToken() && !!getRefreshToken();
}

/**
 * Get API base URL (for direct fetch operations like file downloads)
 */
export function getApiBaseUrl(): string {
  return API_BASE;
}

/**
 * Get auth token (alias for getAccessToken for export API)
 */
export function getAuthToken(): string | null {
  return getAccessToken();
}

/**
 * API Error class
 */
export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Parse error response with validation
 */
async function parseError(response: Response): Promise<ApiRequestError> {
  try {
    const data = await response.json();
    // Validate error shape before using
    if (typeof data === 'object' && data !== null && 'code' in data && 'error' in data) {
      return new ApiRequestError(
        String(data.code),
        String(data.error),
        response.status,
        data.details
      );
    }
    return new ApiRequestError('UNKNOWN_ERROR', `Request failed with status ${response.status}`, response.status);
  } catch {
    return new ApiRequestError('UNKNOWN_ERROR', `Request failed with status ${response.status}`, response.status);
  }
}

/**
 * Refresh access token using stored refresh token
 * Uses a lock mechanism to prevent race conditions with concurrent requests
 */
async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        clearTokens();
        return false;
      }

      const data = await response.json() as RefreshResponse;
      storeTokens(data.accessToken, data.refreshToken);
      return true;
    } catch (error) {
      // Log error for debugging (in production, send to monitoring)
      console.error('Token refresh failed:', error);
      clearTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Make authenticated API request with automatic token refresh
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const accessToken = getAccessToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  let response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // If unauthorized, try to refresh token
  if (response.status === 401 && accessToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry request with new token
      const newToken = getAccessToken();
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });
    } else {
      // Token refresh failed - trigger auth failure callback to redirect to login
      if (onAuthFailure) {
        onAuthFailure();
      }
    }
  }

  if (!response.ok) {
    // If still 401 after refresh attempt, trigger auth failure
    if (response.status === 401 && onAuthFailure) {
      onAuthFailure();
    }
    const error = await parseError(response);
    // Geen rol in de HR-portal: melden, niet uitloggen.
    if (response.status === 403 && error.code === ROLE_NOT_ASSIGNED_CODE && onRoleNotAssigned) {
      onRoleNotAssigned(error.message);
    }
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Auth API functions
 */
export const authApi = {
  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw await parseError(response);
    }

    const data = await response.json() as LoginResponse;
    storeTokens(data.accessToken, data.refreshToken);
    return data;
  },

  /**
   * Logout and revoke session
   */
  async logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    if (refreshToken && accessToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Ignore errors on logout
      }
    }

    clearTokens();
  },

  /**
   * Get current user info
   */
  async me(): Promise<UserResponse> {
    return apiRequest<UserResponse>('/auth/me');
  },

  /**
   * Manually refresh tokens
   */
  async refresh(): Promise<boolean> {
    return refreshAccessToken();
  },
};
